import React, { useState } from "react";
import { Link, useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const Register = () => {
  const { user, register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", referred_by: searchParams.get("ref") || "" });
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/member"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Welcome to Panda Tata!");
      navigate("/member");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const field = (key, label, type = "text") => (
    <div>
      <label className="text-sm font-semibold">{label}</label>
      <input data-testid={`register-${key}`} type={type} required={key !== "phone"} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="mt-1 w-full rounded-xl border-2 border-brand-ink bg-brand-cream px-4 py-3 outline-none focus:ring-2 focus:ring-brand-green" />
    </div>
  );

  return (
    <div className="grid min-h-[calc(100vh-140px)] place-items-center bg-brand-yellow px-5 py-12 grain">
      <div className="w-full max-w-md rounded-3xl border-2 border-brand-ink bg-white p-8 shadow-brutal-lg">
        <h1 className="font-display text-4xl font-black tracking-tight">{t("register_title")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("receipt_hint")}</p>
        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
          {field("name", t("form_name"))}
          {field("email", t("form_email"), "email")}
          {field("phone", t("form_phone"))}
          {field("password", t("password"), "password")}
          <div>
            <label className="text-sm font-semibold">{t("referral_code")} <span className="font-normal text-muted-foreground">({t("optional")})</span></label>
            <input data-testid="register-referral" value={form.referred_by} onChange={(e) => setForm({ ...form, referred_by: e.target.value.toUpperCase() })}
              className="mt-1 w-full rounded-xl border-2 border-brand-ink bg-brand-cream px-4 py-3 uppercase outline-none focus:ring-2 focus:ring-brand-green" />
          </div>
          <button type="submit" disabled={loading} data-testid="register-submit" className="w-full rounded-full border-2 border-brand-ink bg-brand-green py-3 font-bold hover-brutal disabled:opacity-60">
            {loading ? "..." : t("nav_join")}
          </button>
        </form>
        <Link to="/login" className="mt-5 block text-center text-sm font-semibold text-brand-greendark hover:underline">{t("have_account")}</Link>
      </div>
    </div>
  );
};

export default Register;
